// src/components/AnalyticsDetailModal.jsx
// Simple Excel-style drill-down for one Analytics card.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  METRICS, HEADLINE_KEY, filterRows, formatCell, sumBy,
} from "../utils/guestRoomAnalytics";
import { exportAnalyticsExcel } from "../utils/exportAnalyticsExcel";

const inr = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

export default function AnalyticsDetailModal({ metricKey, rows, periodNote, onClose }) {
  const [query, setQuery] = useState("");
  const closeRef = useRef(null);
  const metric = METRICS[metricKey];

  const shownRows = useMemo(() => filterRows(metricKey, rows, query), [metricKey, rows, query]);
  const headlineKey = HEADLINE_KEY[metricKey];
  const summary = headlineKey
    ? `${shownRows.length} rows · ${inr(sumBy(shownRows, headlineKey))}`
    : `${shownRows.length} rows`;

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 md:p-6"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${metric.title} details`}
        className="flex max-h-[88vh] w-full max-w-[1400px] flex-col rounded-lg bg-white shadow-xl"
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-3 md:px-4">
          <div className="mr-auto">
            <h2 className="text-base font-semibold text-gray-900 md:text-lg">{metric.title}</h2>
            <p className="text-xs text-gray-500">
              {summary}{query.trim() ? " (filtered)" : ""}{periodNote ? ` · ${periodNote}` : ""}
            </p>
          </div>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search…"
            aria-label="Search rows"
            className="w-40 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 md:w-56"
          />
          <button
            type="button"
            onClick={() => exportAnalyticsExcel(metricKey, shownRows)}
            disabled={shownRows.length === 0}
            className="rounded bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            Export Excel
          </button>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400"
          >
            Close
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                {metric.columns.map((column) => (
                  <th
                    key={column.label}
                    className={`whitespace-nowrap border border-gray-200 px-2 py-1.5 font-semibold text-gray-700 ${
                      column.type === "money" ? "text-right" : "text-left"
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row) => (
                <tr key={row.id} className="odd:bg-white even:bg-gray-50">
                  {metric.columns.map((column) => (
                    <td
                      key={column.label}
                      className={`whitespace-nowrap border border-gray-200 px-2 py-1 ${
                        column.type === "money" ? "text-right tabular-nums" : ""
                      }`}
                    >
                      {column.type === "money"
                        ? inr(row[column.key])
                        : formatCell(column, row)}
                    </td>
                  ))}
                </tr>
              ))}
              {shownRows.length === 0 && (
                <tr>
                  <td colSpan={metric.columns.length} className="px-3 py-8 text-center text-gray-500">
                    No records.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
